import { useState, RefObject } from 'react'
import { Message } from '../../chat-page/App'
import { WelcomeState } from './WelcomeState'
import { Button } from '../../ui/button'
import { Textarea } from '../../ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { Label } from '../../ui/label'
import { V2Markdown } from './V2Markdown'
import { CornerDownRight, Download, Paperclip } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

function formatFileSize(sizeStr: string | undefined) {
  if (!sizeStr) return null
  const n = parseInt(sizeStr, 10)
  if (Number.isNaN(n)) return null
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

/** Real DB message ids are UUIDs; temp streaming ids cannot be used for /chat/file/{id}. */
function canDownloadWithMessageId(id: string) {
  if (!id || id === 'streaming') return false
  if (id.startsWith('aira_') || id.startsWith('edit_aira_')) return false
  return true
}

function AIMessage({
  msg,
  isLast,
  onRegenerate,
  onContinueResponse,
}: {
  msg: Message
  isLast: boolean
  onRegenerate: (id: string) => void
  onContinueResponse?: () => void
}) {
  const [copied, setCopied] = useState(false)
  const copy = () => {
    navigator.clipboard?.writeText(msg.content)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }
  const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''
  return (
    <div className="msg-wrap w-full">
      <div className="msg-ai-row">
        <div className="ai-icon" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 18 18" fill="none"><path d="M9 1.5L10.6 7.4L16.5 9L10.6 10.6L9 16.5L7.4 10.6L1.5 9L7.4 7.4L9 1.5Z" fill="#000" stroke="#000" strokeWidth="0.4" strokeLinejoin="round"/></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ai-msg-header">AIRA</div>
          <div className="ai-text">
            <V2Markdown content={msg.content} isStreaming={!!msg.isStreaming} className="v2-md" compact />
            {msg.isStreaming && (
              <div className="ai-streaming-indicator" aria-label="AIRA is generating">
                <span className="ai-streaming-dot" />
                <span className="ai-streaming-dot" />
                <span className="ai-streaming-dot" />
              </div>
            )}
          </div>
          {msg.file_name && canDownloadWithMessageId(msg.id) && (
            <div
              className="msg-attachment"
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px solid var(--border-subtle, rgba(0,0,0,0.08))',
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                borderRadius: 8,
                padding: '8px 10px',
                background: 'var(--bg-muted, rgba(0,0,0,0.04))',
              }}
            >
              <Paperclip size={16} style={{ flexShrink: 0, opacity: 0.75 }} aria-hidden />
              <span style={{ fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {msg.file_name}
              </span>
              {formatFileSize(msg.file_size) && (
                <span style={{ fontSize: 12, opacity: 0.65, flexShrink: 0 }}>{formatFileSize(msg.file_size)}</span>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 shrink-0"
                onClick={() => window.open(`${API_BASE}chat/file/${msg.id}`, '_blank')}
              >
                <Download className="w-3.5 h-3.5 mr-1" />
                Download
              </Button>
            </div>
          )}
          {(!msg.isStreaming || isLast) && (
            <div className="ai-actions">
              {!msg.isStreaming && (
                <button className="ai-action-btn" onClick={copy} aria-label="Copy response">
                  <svg viewBox="0 0 14 14" fill="none"><rect x="4" y="4" width="8" height="8" rx="1" stroke="currentColor" strokeWidth="1.2"/><path d="M2 10V3a1 1 0 011-1h7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              )}
              {isLast && (
                <button className="ai-action-btn" onClick={() => onRegenerate(msg.id)} aria-label="Regenerate response">
                  <svg viewBox="0 0 14 14" fill="none"><path d="M2 7A5 5 0 0111 4M11 4V1.5M11 4H8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 7A5 5 0 013 10M3 10v2.5M3 10h2.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  Regenerate
                </button>
              )}
            </div>
          )}
          {msg.responseTruncated && isLast && !msg.isRegenerating && onContinueResponse && (
            <div className="msg-trunc-wrap">
              <p className="msg-trunc-note">This reply hit the model output limit. You can continue in the same thread.</p>
              <button type="button" className="msg-continue-btn" onClick={onContinueResponse}>
                <CornerDownRight size={14} aria-hidden />
                Continue response
              </button>
            </div>
          )}
        </div>
      </div>
      {timestamp && <div className="msg-time">{timestamp}</div>}
    </div>
  )
}

interface ChatMessagesProps {
  messages: Message[]
  isTyping: boolean
  messagesEndRef: RefObject<HTMLDivElement>
  onRegenerate: (messageId: string) => void
  /** Sends `continue` so the backend can resume after max_tokens / length truncation. */
  onContinueResponse?: () => void
  onEditMessage?: (messageId: string, newContent: string) => void
  userName?: string
  onChipClick: (text: string) => void
  welcomeChips?: string[]
  userInitials?: string
}

export function ChatMessages({
  messages,
  isTyping,
  messagesEndRef,
  onRegenerate,
  onContinueResponse,
  onEditMessage,
  userName,
  onChipClick,
  welcomeChips,
  userInitials = 'U',
}: ChatMessagesProps) {
  const visible = messages.filter(m => m.is_deleted !== 'true')
  const [editOpen, setEditOpen] = useState(false)
  const [editMessageId, setEditMessageId] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')

  const openEdit = (msg: Message) => {
    setEditMessageId(msg.id)
    setEditContent(msg.content)
    setEditOpen(true)
  }

  const saveEdit = () => {
    if (!editMessageId) return
    const next = editContent.trim()
    if (!next) return
    onEditMessage?.(editMessageId, next)
    setEditOpen(false)
    setEditMessageId(null)
  }

  return (
    <>
      <div className="chat-messages" role="log" aria-live="polite" aria-label="Conversation">
        {visible.length === 0 && !isTyping ? (
          <WelcomeState userName={userName} onChipClick={onChipClick} chips={welcomeChips} />
        ) : (
          <>
            {visible.map((msg, i, arr) => {
              const isLastUserMessage = msg.sender === 'user' && arr.slice(i + 1).every(m => m.sender === 'aira')
              return msg.sender === 'user' ? (
              <div key={msg.id} className="msg-wrap user-msg w-full">
                <div className="msg-user-row">
                  <div className="bubble-user">
                    {msg.content}
                    {msg.file_name && (
                      <div
                        style={{
                          marginTop: 8,
                          paddingTop: 8,
                          borderTop: '1px solid rgba(255,255,255,0.2)',
                          fontSize: 12,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          opacity: 0.95,
                        }}
                      >
                        <Paperclip size={14} aria-hidden />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{msg.file_name}</span>
                        {formatFileSize(msg.file_size) && <span style={{ opacity: 0.8 }}>{formatFileSize(msg.file_size)}</span>}
                      </div>
                    )}
                  </div>
                  <div className="user-avatar" aria-hidden="true">{userInitials}</div>
                </div>
                {(msg.timestamp || (isLastUserMessage && onEditMessage)) && (
                  <div className="user-meta">
                    {msg.timestamp && (
                      <div className="msg-time" style={{ textAlign: 'right' }}>
                        {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    )}
                    {isLastUserMessage && onEditMessage && (
                      <div className="user-actions">
                        <button className="ai-action-btn" onClick={() => openEdit(msg)} aria-label="Edit your last message">
                          <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
                            <path d="M8.9 2.1l3 3M2 12h3l7.6-7.6a1 1 0 000-1.4l-1.6-1.6a1 1 0 00-1.4 0L2 9v3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          Edit
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <AIMessage
                key={msg.id}
                msg={msg}
                isLast={i === arr.length - 1 && !isTyping}
                onRegenerate={onRegenerate}
                onContinueResponse={onContinueResponse}
              />
            )})}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent
          className="max-w-[calc(100%-2rem)] gap-0 rounded-[var(--r-xl)] border p-6 shadow-[var(--shadow-lg)] sm:max-w-[520px]"
          style={{
            background: 'var(--bg-modal)',
            borderColor: 'var(--border-md)',
            color: 'var(--text-primary)',
          }}
        >
          <DialogHeader className="text-left">
            <DialogTitle className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
              Edit message
            </DialogTitle>
            <DialogDescription className="mt-2" style={{ color: 'var(--text-muted)' }}>
              Update your message below. The assistant will regenerate its reply.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            <Label htmlFor="v2-edit-message" style={{ color: 'var(--text-secondary)' }}>
              Message
            </Label>
            <Textarea
              id="v2-edit-message"
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="min-h-[120px] border focus-visible:ring-0 md:text-sm"
              style={{
                background: 'var(--bg-input)',
                borderColor: 'var(--border-md)',
                color: 'var(--text-primary)',
              }}
              placeholder="Edit your message…"
            />
          </div>
          <DialogFooter className="gap-3 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setEditOpen(false)}
              className="hover:opacity-95"
              style={{
                background: 'var(--bg-card)',
                borderColor: 'var(--border-md)',
                color: 'var(--text-secondary)',
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={saveEdit}
              disabled={!editContent.trim()}
              className="text-white hover:opacity-95 disabled:opacity-60"
              style={{
                background: 'var(--accent)',
                borderColor: 'var(--accent)',
              }}
            >
              Save & Regenerate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
